import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import KeywordPlanner from '../dashboard/components/KeywordPlanner';
import SEOTooltip from '../dashboard/components/SEOTooltip';
import UtilitySidebar from './components/UtilitySidebar';
import BrandEquityDashboard from './components/brand-equity/BrandEquityDashboard';
import apiService from '../../services/api';
import LogoutButton from '../../components/LogoutButton';
import BrandNotificationBell from '../../components/BrandNotificationBell';
import CreditsWidget from '../../components/CreditsWidget';
import { isTrustedOAuthMessage } from '../../utils/oauthMessage';

interface IntegrationStatus {
  has_credentials?: boolean;
  gsc_connected?: boolean;
  gsc_verified_site?: string | null;
  ga4_connected?: boolean;
  ga4_property_id?: string | null;
  ga4_property_name?: string | null;
  integrations_connected?: boolean;
}

export default function UtilityManager() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string>('keyword-planner');
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipContent, setTooltipContent] = useState<{ title: string; content: ReactNode }>({ title: '', content: '' });
  const [checkingIntegrations, setCheckingIntegrations] = useState(true);
  const [showIntegrationGate, setShowIntegrationGate] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState<IntegrationStatus | null>(null);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [validating, setValidating] = useState<'gsc' | 'ga4' | null>(null);
  const [integrationError, setIntegrationError] = useState<string | null>(null);
  const userId = localStorage.getItem('user_id') ?? '';

  const [userName, setUserName] = useState<string>('');
  const [brandName, setBrandName] = useState<string>(() => localStorage.getItem('brand_name') || '');
  const [logoUrl, setLogoUrl] = useState<string | null>(() => {
    const storedLogo = localStorage.getItem("logo_url");
    return storedLogo ? storedLogo.replace(/"/g, '') || null : null;
  });

  useEffect(() => {
    checkIntegrations();
    fetchUser();
  }, []);

  async function fetchUser() {
    if (!userId) {
      setUserName(localStorage.getItem('full_name') || localStorage.getItem('username') || 'User');
      return;
    }
    try {
      const [profile, bizProfile] = await Promise.allSettled([
        apiService.getContactIdentity(userId),
        apiService.getWebsiteProfile(userId),
      ]);
      if (profile.status === 'fulfilled') {
        setUserName(
          profile.value?.data?.full_name ||
          localStorage.getItem('full_name') ||
          localStorage.getItem('username') ||
          'User'
        );
      }
      if (bizProfile.status === 'fulfilled') {
        const bData = bizProfile.value?.data?.business_profile || bizProfile.value?.business_profile || bizProfile.value;
        if (bData?.business_name) {
          setBrandName(bData.business_name);
          localStorage.setItem('brand_name', bData.business_name);
        }
      }
    } catch {
      const raw = localStorage.getItem("logo_url");
      setLogoUrl(raw ? raw.replace(/"/g, '') || null : null);
      setUserName(localStorage.getItem('full_name') || localStorage.getItem('username') || 'User');
    }
  }



  const handleTooltipShow = (title: string, content: ReactNode) => {
    setTooltipContent({ title, content });
    setShowTooltip(true);
  };

  const checkIntegrations = async () => {
    if (!userId) {
      setCheckingIntegrations(false);
      return;
    }

    try {
      setCheckingIntegrations(true);
      const status = await apiService.checkOAuthStatus(userId) as IntegrationStatus;
      setIntegrationStatus(status);
      setShowIntegrationGate(!status.integrations_connected);
    } catch (error: any) {
      setIntegrationError(error.message ?? 'Unable to check Google integrations.');
      setShowIntegrationGate(true);
    } finally {
      setCheckingIntegrations(false);
    }
  };

  const handleOAuthLogin = async () => {
    if (!userId) {
      setIntegrationError('User ID not found.');
      return;
    }

    try {
      setOauthLoading(true);
      setIntegrationError(null);
      const response: any = await apiService.getOAuthUrl(userId);
      const authUrl = response.authorization_url;

      const width = 520;
      const height = 640;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(authUrl, 'Google OAuth', `width=${width},height=${height},left=${left},top=${top}`);

      const handleMessage = async (event: MessageEvent) => {
        if (!isTrustedOAuthMessage(event)) return;
        if (event.data?.type === 'OAUTH_SUCCESS') {
          if (event.data.accessToken) {
            localStorage.setItem('oauth_access_token', event.data.accessToken);
          }
          popup?.close();
          window.removeEventListener('message', handleMessage);
          setOauthLoading(false);
          await checkIntegrations();
        }

        if (event.data?.type === 'OAUTH_ERROR') {
          popup?.close();
          window.removeEventListener('message', handleMessage);
          setOauthLoading(false);
          setIntegrationError(event.data.error || 'Google authentication failed.');
        }
      };

      window.addEventListener('message', handleMessage);
      window.setTimeout(() => {
        window.removeEventListener('message', handleMessage);
        setOauthLoading(false);
      }, 5 * 60 * 1000);
    } catch (error: any) {
      setOauthLoading(false);
      setIntegrationError(error.message ?? 'OAuth initialization failed.');
    }
  };

  const validateIntegration = async (service: 'gsc' | 'ga4') => {
    if (!userId) return;

    try {
      setValidating(service);
      setIntegrationError(null);
      const response: any = await apiService.validateConnections({
        user_id: userId,
        access_token: localStorage.getItem('oauth_access_token') || '',
        target_url: '',
        validate_gsc: service === 'gsc',
        validate_ga4: service === 'ga4'
      });

      const data = response.data ?? {};
      if (service === 'gsc' && !data.gsc_connected) {
        setIntegrationError(data.gsc_error || 'Google Search Console validation failed.');
      }
      if (service === 'ga4' && !data.ga4_connected) {
        setIntegrationError(data.ga4_error || 'Google Analytics validation failed.');
      }
      await checkIntegrations();
    } catch (error: any) {
      setIntegrationError(error.message ?? 'Validation failed.');
    } finally {
      setValidating(null);
    }
  };

  const renderConnectionCard = () => {
    const hasCredentials = Boolean(integrationStatus?.has_credentials);
    const gscConnected = Boolean(integrationStatus?.gsc_connected);
    const ga4Connected = Boolean(integrationStatus?.ga4_connected);

    return (
      <div className="max-w-5xl mx-auto bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-7 border-b border-gray-100 flex items-start justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-lg bg-teal-50 text-teal-700 text-xs font-bold uppercase tracking-widest mb-3">
              <i className="ri-google-fill"></i>
              Optional data boost
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Connect Google data sources</h2>
            <p className="text-sm text-gray-500 mt-2 max-w-2xl">
              Google Search Console and Analytics help the keyword planner prioritize real search visibility, traffic signals, and proven opportunities. You can skip this and continue with generated intelligence.
            </p>
          </div>
          <button
            onClick={() => setShowIntegrationGate(false)}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50"
          >
            Skip for now
          </button>
        </div>

        <div className="p-7 space-y-4">
          {integrationError && (
            <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {integrationError}
            </div>
          )}

          <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${hasCredentials ? 'bg-green-100 text-green-700' : 'bg-white text-gray-500 border border-gray-200'}`}>
                <i className={hasCredentials ? 'ri-checkbox-circle-line text-xl' : 'ri-login-circle-line text-xl'}></i>
              </div>
              <div>
                <div className="font-semibold text-gray-900">Google account access</div>
                <div className="text-xs text-gray-500">Required once for both Search Console and Analytics scopes.</div>
              </div>
            </div>
            <button
              onClick={handleOAuthLogin}
              disabled={oauthLoading}
              className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-60"
            >
              {oauthLoading ? 'Connecting...' : hasCredentials ? 'Reconnect' : 'Connect Google'}
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="p-5 rounded-lg border border-gray-200 bg-white">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-bold text-gray-900 flex items-center gap-2">
                    <i className="ri-search-eye-line text-blue-600"></i>
                    Search Console
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">Validates ownership and unlocks real ranking/query signals.</p>
                  {integrationStatus?.gsc_verified_site && (
                    <p className="text-xs text-gray-500 mt-2 truncate">{integrationStatus.gsc_verified_site}</p>
                  )}
                </div>
                {gscConnected && <i className="ri-checkbox-circle-fill text-green-600 text-2xl"></i>}
              </div>
              {!gscConnected && (
                <button
                  onClick={() => validateIntegration('gsc')}
                  disabled={!hasCredentials || validating === 'gsc'}
                  className="mt-4 w-full px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400"
                >
                  {validating === 'gsc' ? 'Validating...' : 'Validate GSC'}
                </button>
              )}
            </div>

            <div className="p-5 rounded-lg border border-gray-200 bg-white">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-bold text-gray-900 flex items-center gap-2">
                    <i className="ri-line-chart-line text-orange-600"></i>
                    Google Analytics 4
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">Adds behavior and engagement context to prioritization.</p>
                  {integrationStatus?.ga4_property_name && (
                    <p className="text-xs text-gray-500 mt-2 truncate">{integrationStatus.ga4_property_name}</p>
                  )}
                </div>
                {ga4Connected && <i className="ri-checkbox-circle-fill text-green-600 text-2xl"></i>}
              </div>
              {!ga4Connected && (
                <button
                  onClick={() => validateIntegration('ga4')}
                  disabled={!hasCredentials || validating === 'ga4'}
                  className="mt-4 w-full px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 disabled:bg-gray-200 disabled:text-gray-400"
                >
                  {validating === 'ga4' ? 'Checking...' : 'Validate GA4'}
                </button>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={checkIntegrations}
              className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50"
            >
              Refresh status
            </button>
            <button
              onClick={() => setShowIntegrationGate(false)}
              className="px-5 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700"
            >
              Continue to Utility Manager
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (checkingIntegrations) {
      return (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center shadow-sm">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-sm font-medium text-gray-600">Checking Google integrations...</p>
        </div>
      );
    }

    if (showIntegrationGate) {
      return renderConnectionCard();
    }

    switch (activeSection) {
      case 'brand-equity':
        return <BrandEquityDashboard userId={userId} />;
      case 'keyword-planner':
      default:
        return <KeywordPlanner onTooltipShow={handleTooltipShow} />;
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-gray-50">
      <div className="flex h-full">
        <UtilitySidebar activeSection={activeSection} setActiveSection={setActiveSection} />

        <div className="flex-1 flex flex-col h-full">
          <header className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/marketing-intricacies')}
                className="w-9 h-9 rounded-lg bg-gray-900 text-white flex items-center justify-center hover:bg-gray-800 transition-colors"
                aria-label="Back to Marketing Intricacies"
              >
                <i className="ri-arrow-left-line text-lg"></i>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Utility Manager</h1>
                {/* <p className="text-sm text-gray-500">
                  {activeSection === 'brand-equity'
                    ? 'Brand Equity Index powered by cross-module data analysis.'
                    : 'Keyword planning utility powered by the existing SEO keyword universe.'}
                </p> */}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowIntegrationGate(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              >
                <i className="ri-links-line"></i>
                Integrations
              </button>
              <button
                onClick={() => navigate('/marketing-intricacies')}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              >
                <i className="ri-home-line"></i>
                Home
              </button>
              <BrandNotificationBell />
              <CreditsWidget />
              <button
                onClick={() => navigate('/profile')}
                className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full hover:bg-gray-100 transition-colors cursor-pointer group"
                title="Profile"
              >
                <div
                  className={`w-8 h-8 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 ${logoUrl
                    ? 'bg-white border border-gray-200'
                    : 'bg-teal-100'
                  }`}
                >
                  {logoUrl ? (
                    <img src={logoUrl} alt="Business Logo" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-teal-600 font-semibold text-sm">
                      {userName?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  )}
                </div>
                <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 whitespace-nowrap" title="Profile">
                  Profile
                </span>
                <i className="ri-arrow-right-s-line text-gray-400 text-base"></i>
              </button>
              <LogoutButton className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-all">
                <i className="ri-logout-box-r-line text-[18px] text-slate-600"></i>
                <span className="hidden lg:inline text-sm font-semibold text-slate-700">Logout</span>
              </LogoutButton>
            </div>
          </header>

          <main className="flex-1 p-6 overflow-auto">
            {renderContent()}
          </main>
        </div>
      </div>

      {showTooltip && (
        <SEOTooltip
          title={tooltipContent.title}
          content={tooltipContent.content}
          onClose={() => setShowTooltip(false)}
          darkMode={false}
        />
      )}
    </div>
  );
}
